// In-memory ring buffer that captures recent console output so we can attach
// it to bug reports. Data never leaves the user's browser unless they
// explicitly submit a report.
//
// Capacity: 20 entries. Each entry message capped at 500 chars.

export type ConsoleEntry = {
  level: "log" | "warn" | "error" | "info";
  message: string;
  ts: string; // ISO timestamp
};

const MAX_ENTRIES = 20;
const MAX_MSG_LEN = 500;

// ---- PII / secret scrubbing ----------------------------------------------
// Best-effort regex-based filter. We intentionally hedge in the privacy
// policy ("reasonable efforts") because regex can never be exhaustive.
const REDACTED = "[redacted]";

const JWT_RE = /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g;
const BEARER_RE = /\b(bearer\s+)[A-Za-z0-9._\-+/=]+/gi;
const KEYED_SECRET_RE =
  /\b(api[_-]?key|access[_-]?token|refresh[_-]?token|secret|password|authorization)\b(\s*[:=]\s*)["']?[A-Za-z0-9._\-+/=]{6,}["']?/gi;
const EMAIL_RE = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g;
const SUPABASE_COOKIE_RE = /sb-[a-z0-9-]+-auth-token=[^;\s"']+/gi;

export function scrubSensitive(input: string): string {
  if (!input) return input;
  return input
    .replace(JWT_RE, REDACTED)
    .replace(SUPABASE_COOKIE_RE, (m) => m.split("=")[0] + "=" + REDACTED)
    .replace(BEARER_RE, (_m, p1) => `${p1}${REDACTED}`)
    .replace(KEYED_SECRET_RE, (_m, key, sep) => `${key}${sep}${REDACTED}`)
    .replace(EMAIL_RE, REDACTED);
}

let buffer: ConsoleEntry[] = [];
let installed = false;

function safeStringify(arg: unknown): string {
  if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
  if (typeof arg === "string") return arg;
  if (arg === null || arg === undefined) return String(arg);
  try {
    return JSON.stringify(arg);
  } catch {
    return String(arg);
  }
}

function record(level: ConsoleEntry["level"], args: unknown[]) {
  const raw = args.map(safeStringify).join(" ");
  const msg = scrubSensitive(raw).slice(0, MAX_MSG_LEN);
  buffer.push({ level, message: msg, ts: new Date().toISOString() });
  if (buffer.length > MAX_ENTRIES) buffer.shift();
}

/**
 * Patch console.{log,info,warn,error} to mirror into the ring buffer.
 * Idempotent — safe to call multiple times.
 */
export function installConsoleBuffer(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  const levels: ConsoleEntry["level"][] = ["log", "info", "warn", "error"];
  for (const level of levels) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const original = (console as any)[level]?.bind(console);
    if (!original) continue;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (console as any)[level] = (...args: unknown[]) => {
      try {
        record(level, args);
      } catch {
        /* swallow */
      }
      original(...args);
    };
  }

  // Also capture uncaught errors and unhandled promise rejections.
  window.addEventListener("error", (e) => {
    record("error", [
      `[uncaught] ${e.message}`,
      e.filename ? `(${e.filename}:${e.lineno}:${e.colno})` : "",
    ]);
  });
  window.addEventListener("unhandledrejection", (e) => {
    record("error", [`[unhandledrejection]`, e.reason]);
  });
}

export function readConsoleBuffer(): ConsoleEntry[] {
  return [...buffer];
}

export function clearConsoleBuffer(): void {
  buffer = [];
}
